/**
 * @author: lv.chen
 * @date: 2025/4/1
 * @desc: Pipeline / Change Log页面 Change Checklist页签 表格组件
 */
import React, { useMemo, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useLatest } from 'ahooks';
import { Table, TableProps, TableColumn, TableAddon, PagingQuery } from 'tea-component';
import useTableAutotipAddon from '@gp/hooks/useTableAutotipAddon';
import useTablePageableOptionsAddon from '@gp/hooks/useTablePageableOptionsAddon';
import { getTableDataCellElementAttrs } from './utils';
import { getColumns, analysisDynamicMetadata } from './utils/columns';
import { PAGE_SIZES } from '@gp/constants/const';

import type { SortBy } from 'tea-component/lib/table/addons';
import type { IBaseProps } from '@home/types/common';
import type {
  IPipelineChangeChecklistFilterVO,
  IPipelineChangeChecklistRecordDTO,
  IPipelineChangeChecklistRecordVO,
} from '@gp/types/changeLog';

import './index.scss';

interface IChangeChecklistTableProps extends IBaseProps, Omit<TableProps, 'addons' | 'columns' | 'records'> {
  loading?: boolean;
  error?: Error;
  isSearch?: boolean;
  filter?: IPipelineChangeChecklistFilterVO;
  total?: number;
  data?: IPipelineChangeChecklistRecordDTO[] | null;
  sort?: SortBy<IPipelineChangeChecklistRecordVO>[];
  pagination?: PagingQuery;
  onSortChange?: (payload: SortBy<IPipelineChangeChecklistRecordVO>[]) => void;
  onPaginationChange?: (payload?: PagingQuery) => void;
  onRetry?: () => void;
  onReset?: () => void;
}

const { groupable, injectable, sortable } = Table.addons;

const recordKey = (record?: IPipelineChangeChecklistRecordVO) => record?.project_id || '';

const ChangeChecklistTable: React.FC<IChangeChecklistTableProps> = (props) => {
  const {
    style,
    className,
    loading = false,
    error = false,
    isSearch = false,
    filter,
    total = 0,
    data = [],
    pagination,
    sort,
    onSortChange,
    onPaginationChange,
    onRetry,
    onReset,
    tableHeight,
  } = props;

  const onSortChangeLatest = useLatest(onSortChange);

  const { headerGroups: dynamicHeaderGroups, columns: dynamicColumns } = useMemo(() => {
    return analysisDynamicMetadata(filter);
  }, [filter]);

  const records = useMemo(() => {
    if (loading) return [];
    if (!!error) return [];
    if (!Array.isArray(data) || !data.length) return [];
    return data;
  }, [loading, error, data]);

  const autotipAddon = useTableAutotipAddon({
    isLoading: loading,
    isError: !!error,
    onRetry,
    isFound: !records?.length,
    onClear: onReset,
    isUseResetNodata: true,
  });

  const pageableAddon = useTablePageableOptionsAddon<IPipelineChangeChecklistRecordVO>({
    ...pagination,
    loading,
    pageIndex: isSearch ? 1 : pagination?.pageIndex,
    recordCount: isSearch ? 1 : total,
    pageSizeOptions: PAGE_SIZES,
    onPagingChange: onPaginationChange,
  });

  const groupableAddon = useMemo<TableAddon<IPipelineChangeChecklistRecordVO>>(() => {
    return groupable({ headerGroups: dynamicHeaderGroups });
  }, [dynamicHeaderGroups]);

  const injectableAddon = useMemo(() => {
    return injectable({
      cell: (props, context) => {
        // 多个日期的Update Status组合列板块 间隔 展示 不同的背景色、勾勾颜色，提升数据的展示的直观性
        const dataCellAttrs = getTableDataCellElementAttrs(props, context);
        return { ...props, ...dataCellAttrs };
      },
    });
  }, []);

  const sortableAddon = useMemo<TableAddon<IPipelineChangeChecklistRecordVO>>(() => {
    return sortable({
      columns: ['priority', 'pteam'],
      value: sort,
      onChange: (payload) => {
        onSortChangeLatest?.current?.(payload);
      },
    });
  }, [sort]);

  const addons = useMemo<TableAddon<IPipelineChangeChecklistRecordVO>[]>(() => {
    return [autotipAddon, groupableAddon, injectableAddon, sortableAddon, pageableAddon];
  }, [autotipAddon, groupableAddon, injectableAddon, sortableAddon, pageableAddon]);

  const columns = useMemo<TableColumn<IPipelineChangeChecklistRecordVO>[]>(() => {
    const staticColumns = getColumns();
    return [...staticColumns, ...dynamicColumns];
  }, [dynamicColumns]);

  const isReady = useMemo(() => {
    return loading || Boolean(error);
  }, [loading, error]);

  const styleCur = useMemo(() => {
    const newStyle = !isReady ? { overflow: 'auto', maxHeight: `${tableHeight}px` } : null;
    return {
      ...style,
      ...newStyle,
    };
  }, [isReady, style, tableHeight]);

  return (
    <Table
      // style={styleCur}
      className={classnames(
        'dbh-action-state-center-table dbg-action-state-center-table dbg-tab-page__table dbg-sortable-table dbg-pageable-table dbg-autotip-table dbg-pipeline-change-log-change-checklist-table',
        className,
      )}
      verticalTop
      addons={addons}
      columns={columns}
      recordKey={recordKey}
      records={records}
    />
  );
};

export default ChangeChecklistTable;
